import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Button,
  Modal
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import RoomIcon from '@mui/icons-material/Room';
import ModalProductos from "../componentes/modalproducto";
import ModalDetalleProducto from '../componentes/modaldetalleproducto';

export default function TablaProductos() {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [usuario, setUsuario] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  useEffect(() => {
    const storedUser = localStorage.getItem("usuario");
    if (storedUser) {
      try { setUsuario(JSON.parse(storedUser)); } 
      catch (e) { console.error("Error al parsear usuario:", e); }
    }
  }, []);

  useEffect(() => {
    if (usuario) getProductos(usuario.empresa_idempresa);
  }, [usuario]);

  const getProductos = async (empresaId) => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.post(process.env.REACT_APP_API_PRODUCTOS, { empresa_id: empresaId });
      setProductos(res.data);
    } catch (err) {
      console.error(err);
      setError("Error al obtener productos");
    } finally {
      setLoading(false);
    }
  };

  const deleteProducto = async (idProducto) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    try {
      await axios.post(`${process.env.REACT_APP_API_PRODUCTOS_ELIMINAR}?id=${idProducto}`);
      getProductos(usuario.empresa_idempresa);
    } catch (err) {
      console.error(err);
      setError("Error al eliminar producto");
    }
  };

  const openImageModal = (image) => { setSelectedImage(image); setShowImageModal(true); };
  const closeImageModal = () => { setSelectedImage(''); setShowImageModal(false); };

  const filteredData = useMemo(() => {
    return productos.filter((p) =>
      [p.descripcion, p.epc, p.lugar].join(" ").toLowerCase().includes(search.toLowerCase())
    );
  }, [productos, search]);

  if (!usuario) return <Typography>Cargando usuario...</Typography>;

  return (
    <Box sx={{ p: 3 }}>
      {/* HEADER */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>Productos</Typography>
          <Typography variant="body2" color="text.secondary">
            Gestión y administración de productos registrados
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <TextField
            size="small"
            placeholder="Buscar producto..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Stack>
      </Stack>

      {/* ERROR */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* LOADING */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 4px 20px rgba(0,0,0,0.05)"
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ background: "linear-gradient(90deg, #1976d2, #42a5f5)" }}>
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>Descripción</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>Estado</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>EPC</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>Ubicación</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>Fecha</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>Mapa</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>Foto</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>Acciones</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">No se encontraron productos</TableCell>
                </TableRow>
              ) : (
                filteredData.map((p) => (
                  <TableRow key={p.idProductos} sx={{ "&:hover": { backgroundColor: "rgba(25, 118, 210, 0.08)" } }}>
                    <TableCell>{p.descripcion}</TableCell>
                    <TableCell>{p.estado}</TableCell>
                    <TableCell>{p.epc}</TableCell>
                    <TableCell>{p.lugar}</TableCell>
                    <TableCell>{p.fechaproducto}</TableCell>
                    <TableCell>
                      <Tooltip title="Ver en mapa">
                        <IconButton
                          component="a"
                          href={`https://www.google.com/maps?q=${p.latitud},${p.longitud}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <RoomIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <img
                        src={p.foto}
                        alt="Producto"
                        style={{ width: 60, height: 60, cursor: "pointer", borderRadius: 8, border: "1px solid #ddd" }}
                        onClick={() => openImageModal(p.foto)}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <ModalProductos producto={p} />
                        <ModalDetalleProducto producto={p} />
                        <Tooltip title="Eliminar">
                          <IconButton color="error" onClick={() => deleteProducto(p.idProductos)}>
                            <DeleteForeverIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* MODAL IMAGEN */}
      <Modal open={showImageModal} onClose={closeImageModal} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Box sx={{ width: "90%", maxWidth: 500, outline: "none", borderRadius: 3, p: 2, bgcolor: "background.paper" }}>
          <Typography variant="h6" mb={2}>Imagen del producto</Typography>
          <img src={selectedImage} alt="preview" style={{ width: "100%", borderRadius: 8 }} />
          <Box mt={2} textAlign="right">
            <Button variant="contained" onClick={closeImageModal}>Cerrar</Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}